import { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import PhaseBadge from '@/components/feature/PhaseBadge';
import { PHASES } from '@/mocks/schedule';
import { formatCnRange } from '@/utils/date';

export default function PhaseOverview() {
  const defaultId = PHASES.find((p) => p.status === 'active')?.id ?? PHASES[0].id;
  const [activeId, setActiveId] = useState(defaultId);
  const active = PHASES.find((p) => p.id === activeId) ?? PHASES[0];

  return (
    <section id="phases" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100" aria-labelledby="phases-heading">
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          id="phases-heading"
          eyebrow="Roadmap"
          title="赛程阶段总览，七个阶段一条线"
          desc="点击任意阶段，查看该阶段的时间区间、核心任务与需要做什么。全部节点同步维护在赛程时间表页。"
        />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5">
            <ol className="flex flex-col gap-2">
              {PHASES.map((p, idx) => {
                const isActive = p.id === active.id;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(p.id)}
                      aria-current={isActive ? 'step' : undefined}
                      className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-md text-left transition-colors cursor-pointer ${
                        isActive
                          ? 'bg-background-50 border border-primary-300'
                          : 'bg-background-50/60 border border-background-200 hover:border-primary-200'
                      }`}
                    >
                      <span
                        className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-md font-heading text-sm font-bold ${
                          isActive ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground-950 truncate">{p.name}</span>
                        <span className="block text-xs text-foreground-500 mt-0.5">{p.tag}</span>
                      </span>
                      <PhaseBadge status={p.status} />
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 h-full flex flex-col">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-heading text-5xl font-bold text-background-300">{PHASES.findIndex((p) => p.id === active.id) + 1}</span>
                <div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{active.name}</h3>
                  <p className="text-xs text-foreground-500 mt-1">{active.tag}</p>
                </div>
                <span className="ml-auto">
                  <PhaseBadge status={active.status} />
                </span>
              </div>

              <p className="mt-5 inline-flex items-center gap-2 text-sm text-primary-700 font-medium">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-event-line"></i>
                </span>
                {formatCnRange(active.start, active.end)}
              </p>

              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{active.summary}</p>

              <ul className="mt-6 space-y-3">
                {active.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <span className="w-5 h-5 shrink-0 flex items-center justify-center mt-0.5">
                      <i className="ri-checkbox-circle-line text-secondary-500"></i>
                    </span>
                    {pt}
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-6">
                <div className="rounded-md bg-accent-100 border border-accent-300 px-4 py-3 flex items-start gap-2.5">
                  <span className="w-5 h-5 shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-alarm-warning-line text-accent-800"></i>
                  </span>
                  <p className="text-sm text-accent-900">{active.action}</p>
                </div>
                <Link
                  to="/schedule"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  在时间表中定位这个阶段
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}